import React, { useState } from 'react';
import { DashGridItem, ChartTypeValues, ChartTypeOption, DashGridItemFields, IconTypeValues } from '../../DashGrid';
import './BuildForm.scss';
import { Dropdown, Icon, Modal, Button, Checkbox } from 'semantic-ui-react';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { SemanticUIColor } from '@src/types';
import { FinePrintMessage } from '@src/views/components/util/messaging/FinePrintMessage';
import { BasicDialog } from '@src/views/components/modals/BasicDialog';

interface BuildFormProps {
    procedures: any[];
    /** Options for the Chart Type select; defaults to every chart type */
    chartTypes?: ReadonlyArray<ChartTypeOption>;
    initialItem?: Partial<DashGridItem>;
    onSubmit?: (item: Partial<DashGridItem>) => void;
    onCloseForm?: () => void;
    /** When true, renders the form directly without the BasicDialog wrapper (for use in panels) */
    standalone?: boolean;
    /** HTML id applied to <form> so an external button can submit via form="id" */
    formId?: string;
    /** When true, the Save/Close buttons inside the form are suppressed (use formId + external footer buttons instead) */
    hideActions?: boolean;
}

export const BuildForm: React.FC<BuildFormProps> = ({
    initialItem = {},
    onSubmit,
    procedures,
    chartTypes = ChartTypeValues,
    onCloseForm,
    standalone = false,
    formId,
    hideActions = false,
}) => {
    const [formData, setFormData] = useState<Partial<DashGridItem>>(initialItem);

    const handleInputChange = (key: keyof DashGridItem, value: any) => {
        const fieldDefinition = DashGridItemFields.find((field) => field.idName === key);
        // Check if the field's controlType is 'number' and convert the value
        const parsedValue = fieldDefinition?.controlType === 'number' && value !== '' ? Number(value) : value;
        setFormData((prev) => ({ ...prev, [key]: parsedValue }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (onSubmit) onSubmit(formData);
    };

    const renderField = (key: keyof DashGridItem, controlType: string, disable: boolean) => {
        if (controlType === 'enum') {
            return (
                <select
                    value={formData[key] || ''}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                >
                    <option value="" disabled>Select Chart Type</option>
                    {chartTypes.map(({ display, value }) => (
                        <option key={value} value={value}>{display}</option>
                    ))}
                </select>
            );
        }
        if (controlType === 'procedures' && procedures) {
            return (
                <select
                    value={formData[key] || ''}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                >
                    <option value="" disabled>Select Procedure</option>
                    {procedures
                        .slice() // Make a copy to avoid mutating the original array
                        .sort((a, b) => a.displayName.localeCompare(b.displayName)) // Sort by displayName
                        .map(({ displayName, procedureName, id }) => (
                            <option key={id} value={procedureName}>{displayName}</option>
                        ))
                    }
                </select>
            );
        }
        if (controlType === 'icon') {
            const options = IconTypeValues.map(({ display, value }) => ({
                key: value,
                value,
                text: display,
                content: (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Icon name={value} style={{ marginRight: 8 }} />
                        {display}
                    </div>
                ),
            }));
            return (
                <Dropdown
                    placeholder="Select an Icon"
                    fluid
                    selection
                    options={options}
                    value={formData[key] || ''}
                    onChange={(e, { value }) => handleInputChange(key, value as string)}
                />
            );
        }
        if (controlType === 'boolean') {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FinePrintMessage style={{ marginRight: '8px' }}>{key}:</FinePrintMessage>
                    <Checkbox
                        toggle
                        checked={!!formData[key]}
                        disabled={disable}
                        onChange={(e, { checked }) => handleInputChange(key, checked)}
                    />
                </div>
            );
        }
        return (
            <input
                type={controlType === 'number' ? 'number' : 'text'}
                value={formData[key] || ''}
                disabled={disable ? true : false}
                onChange={(e) => handleInputChange(key, e.target.value)}
            />
        );
    };

    const formContent = (
        <form id={formId} onSubmit={handleSubmit}>
            <div className="build-form">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {DashGridItemFields.filter(({ hide }) => !hide).map(({ idName, displayName, controlType, size, disable }) => (
                        <div
                            key={idName}
                            className="form-group"
                            style={{ flex: size === 'half' ? '1 1 48%' : '1 1 100%' }}
                        >
                            {controlType !== 'boolean' && <FinePrintMessage>{displayName}:</FinePrintMessage>}
                            {renderField(idName as keyof DashGridItem, controlType, disable)}
                        </div>
                    ))}
                </div>
                {!hideActions && (
                    <div className="form-actions">
                        <Button type="submit" className="submit-btn">Save</Button>
                        <Button type="button" className="reset-btn" onClick={onCloseForm}>Close</Button>
                    </div>
                )}
            </div>
        </form>
    );

    if (standalone) {
        return formContent;
    }

    return (
        <BasicDialog
            showCancelButton={false}
            showSubmitButton={false}
            submitLabel=""
            submitColor={SemanticUIColor.PURPLE}
            className="no-padding dash-dialog"
        >
            <BasicDialogContainer title="Edit Widget" titleColor={SemanticUIColor.PURPLE}>
                {formContent}
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default BuildForm;